import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AimsLogo } from '@aimsos/ui';
import useAuth from './useAuth.js';
import { getCurrentSlugFromHostname } from './subdomain.js';
import { stashPendingWorkspaceRedirect } from './pendingWorkspaceRedirect.js';

/**
 * Branded identity-login screen.
 *
 * Auth0 is used ONLY for this one-time identity step, and only ever from
 * ONE fixed origin (VITE_APP_BASE_URL) — never from a workspace subdomain
 * directly. That's what keeps its Allowed Callback URLs list to a single
 * fixed entry instead of needing a wildcard (or a manually-maintained list
 * of every workspace subdomain, which isn't viable since slugs are created
 * dynamically): if this page is reached on {slug}.{rootDomain} (someone
 * typed the workspace URL directly while signed out), it immediately
 * bounces to the base URL's /login carrying that slug in the query string
 * instead of rendering its own "Sign in" button — the base origin stashes
 * the slug (see pendingWorkspaceRedirect.js) and does the actual identity
 * login, then WorkspaceGate hands the resulting JWT off to the originally-
 * requested subdomain (see tokenHandoff.js) once it's ready.
 *
 * After that: no re-invoking Auth0 when moving between workspace
 * subdomains (first landing after login, or a workspace switch) — those
 * hand our own backend JWT directly to the destination origin instead.
 */
/** Survives the Auth0 round trip, which returns to a fixed callback URL. */
const NEXT_KEY = 'aims_login_next_path';

/**
 * Where the visitor was headed before the guard sent them here.
 *
 * `ProtectedRoute` carries it as `?next=`; `window.location.pathname` cannot
 * stand in for it, because by the time this page renders that already says
 * `/login`. Auth0 then returns to one fixed callback URL with no query of
 * ours on it, so the value is mirrored into `sessionStorage` for the trip
 * and read back from there when the query is gone.
 */
function readNextPath() {
  if (typeof window === 'undefined') return '';
  const fromQuery = new URLSearchParams(window.location.search).get('next') || '';
  // Same-origin paths only: an absolute URL here would be an open redirect.
  const safe = (p) => (p.startsWith('/') && !p.startsWith('//') ? p : '');

  if (safe(fromQuery)) {
    try {
      window.sessionStorage.setItem(NEXT_KEY, fromQuery);
    } catch {
      // Private mode — the visitor lands on the default page instead.
    }
    return fromQuery;
  }

  try {
    return safe(window.sessionStorage.getItem(NEXT_KEY) || '');
  } catch {
    return '';
  }
}

/** Drop the mirror, so a later visit to `/login` does not reuse a stale path. */
function clearNextPath() {
  try {
    window.sessionStorage.removeItem(NEXT_KEY);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
}

const LoginPage = () => {
  const { loginWithRedirect, isLoading, isAuthenticated, error } = useAuth();
  const [redirectingToBase, setRedirectingToBase] = useState(false);
  const [nextPath] = useState(readNextPath);

  useEffect(() => {
    if (isAuthenticated) return;
    const slug = getCurrentSlugFromHostname();
    if (!slug) return;

    const baseUrl = (import.meta.env.VITE_APP_BASE_URL || '').replace(/\/$/, '');
    if (!baseUrl) return; // no known base origin to bounce to — fall back to signing in here

    setRedirectingToBase(true);
    // `nextPath`, not `window.location` — the guard already replaced the URL
    // with `/login`, so reading it here would send the visitor back to the
    // login page after authenticating instead of where they were going.
    const params = new URLSearchParams({ returnSlug: slug, returnPath: nextPath || '/' });
    window.location.href = `${baseUrl}/login?${params.toString()}`;
  }, [isAuthenticated, nextPath]);

  // The mirror has done its job the moment a session exists; `nextPath` is
  // already in state. Clearing here rather than beside the `<Navigate>` below
  // keeps render free of side effects — and keeps a later, path-less visit to
  // `/login` from inheriting this one's destination.
  useEffect(() => {
    if (isAuthenticated) clearNextPath();
  }, [isAuthenticated]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const returnSlug = params.get('returnSlug');
    if (!returnSlug) return;
    stashPendingWorkspaceRedirect({ slug: returnSlug, path: params.get('returnPath') || '/' });
    // Drop the query string so it doesn't linger in the address bar / get
    // re-processed on a later render.
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  if (isAuthenticated) {
    return <Navigate to={nextPath || '/dashboard'} replace />;
  }

  if (redirectingToBase) {
    return (
      <div className="min-h-screen relative flex flex-col items-center justify-center px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-950 via-purple-950 to-teal-950" />
        <Loader2 size={24} className="relative z-10 animate-spin text-white/70" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center px-4 overflow-hidden">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-950 via-purple-950 to-teal-950" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/40 via-transparent to-transparent" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-teal-600/20 rounded-full blur-3xl" />

      {/* Card */}
      <div className="relative z-10 w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <AimsLogo size={56} className="mb-4 drop-shadow-2xl" />
          <h1 className="text-2xl font-bold text-white tracking-tight">AIMS OS</h1>
          <p className="text-gray-400 text-sm mt-1">Intelligent automation platform</p>
        </div>

        {/* Sign in card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-white mb-2">Welcome back</h2>
            <p className="text-gray-400 text-sm leading-relaxed">
              Sign in to access your account and continue with the ANN experience.
            </p>
          </div>

          {error && (
            <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {error.message || 'Authentication error. Please try again.'}
            </div>
          )}

          <button
            onClick={() => loginWithRedirect()}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 active:from-blue-700 active:to-blue-800 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-all duration-200 shadow-lg shadow-blue-500/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-transparent"
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>Signing in…</span>
              </>
            ) : (
              <span>Sign in</span>
            )}
          </button>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center space-y-2">
          <div className="flex items-center justify-center gap-3 text-sm">
            <a
              href="#"
              className="text-gray-500 hover:text-gray-300 transition-colors"
            >
              Privacy policy
            </a>
            <span className="text-gray-700">·</span>
            <a
              href="#"
              className="text-gray-500 hover:text-gray-300 transition-colors"
            >
              Terms of use
            </a>
          </div>
          <p className="text-gray-600 text-xs">Powered by AIMS OS</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
